import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { useLocation, useNavigate } from "react-router-dom";

import CvToolbar, { STATUS_OPTIONS } from "./components/CvToolbar";
import CvSubmissionsGrid from "./components/CvSubmissionsGrid";
import CvEmailRequests from "./components/CvEmailRequests";
import CvAdminReviewTour from "../../components/onboarding/CvAdminReviewTour";

import { useCvSubmissionsList } from "./hooks/useCvSubmissionsList";
import { cvAdminApi } from "./api/cvAdminApi";
import { safeMsg } from "./utils/cvUtils";

function StatCard({ icon, label, value, color }) {
  const theme = useTheme();
  const main = theme.palette[color]?.main || theme.palette.primary.main;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        height: "100%",
        bgcolor: alpha(theme.palette.background.paper, 0.92),
        borderColor: alpha(main, 0.35),
        transition: "transform .15s ease, box-shadow .15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 24px ${alpha(main, 0.18)}`,
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(main, 0.14),
            color: main,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {label}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function CvReviewPage() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [tourRestartSignal, setTourRestartSignal] = useState(0);

  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const notify = (severity, message) => setToast({ open: true, severity, message });

  const [rejectDialog, setRejectDialog] = useState({ open: false, row: null });
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const { rows, loading: listLoading, err: listErr, fetchList } = useCvSubmissionsList({
    q,
    statusValue: status?.value ?? "",
    perPage: 200,
    debounceMs: 350,
  });

  const headerBg = useMemo(
    () =>
      `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(
        theme.palette.info.main,
        0.12
      )} 45%, ${alpha(theme.palette.success.main, 0.12)} 100%)`,
    [theme]
  );

  const stats = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const isPending = (s) => ["submitted", "pending", "reopened"].includes(s);
    let pending = 0;
    let approved = 0;
    let rejected = 0;
    let open = 0;
    for (const r of list) {
      const s = String(r?.status || "").trim().toLowerCase();
      if (isPending(s)) pending += 1;
      else if (s === "approved") approved += 1;
      else if (s === "rejected") rejected += 1;
      if (r?.window_open) open += 1;
    }
    return { total: list.length, pending, approved, rejected, open };
  }, [rows]);

  const forceTour = useMemo(
    () => new URLSearchParams(location.search).get("tour") === "1",
    [location.search]
  );

  const handleTourFinish = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tour") !== "1") return;

    params.delete("tour");
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const replayTour = useCallback(() => {
    setTourRestartSignal((prev) => prev + 1);
  }, []);

  const handleView = useCallback((r) => navigate(`/cv/review/${r.user_id}`), [navigate]);
  const handleEdit = useCallback((r) => navigate(`/cv/review/${r.user_id}/edit`), [navigate]);

  const handleApprove = useCallback(
    async (r) => {
      try {
        await cvAdminApi.approve(r.user_id);
        notify("success", `Approved CV for ${r.name || r.employee_code || "employee"}.`);
        fetchList();
      } catch (e) {
        notify("error", safeMsg(e, "Failed to approve CV."));
      }
    },
    [fetchList]
  );

  const openReject = useCallback((r) => {
    setRejectReason("");
    setRejectDialog({ open: true, row: r });
  }, []);

  const closeReject = useCallback(() => {
    if (rejecting) return;
    setRejectDialog({ open: false, row: null });
    setRejectReason("");
  }, [rejecting]);

  const confirmReject = useCallback(async () => {
    const row = rejectDialog.row;
    if (!row) return;
    setRejecting(true);
    try {
      await cvAdminApi.reject(row.user_id, rejectReason.trim());
      notify("success", `Rejected CV for ${row.name || row.employee_code || "employee"}.`);
      setRejectDialog({ open: false, row: null });
      setRejectReason("");
      fetchList();
    } catch (e) {
      notify("error", safeMsg(e, "Failed to reject CV."));
    } finally {
      setRejecting(false);
    }
  }, [rejectDialog.row, rejectReason, fetchList]);

  return (
    <Box sx={{ p: 2.5 }}>
      <CvAdminReviewTour
        isReady={!listLoading}
        forceStart={forceTour}
        restartSignal={tourRestartSignal}
        onFinish={handleTourFinish}
      />

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.background.paper, 0.82),
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.75 },
            background: headerBg,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2.5 }}
          >
            <Box data-tour="cv-review-toolbar">
              <CvToolbar
                q={q}
                setQ={setQ}
                status={status}
                setStatus={setStatus}
                loading={listLoading}
                onRefresh={fetchList}
                onApply={fetchList}
              />
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              data-tour="cv-review-email-actions"
            >
              <Tooltip title="Replay guide">
                <IconButton
                  data-tour="cv-review-help"
                  onClick={replayTour}
                  size="small"
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.9),
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <CvEmailRequests notify={notify} onDone={fetchList} />
            </Stack>
          </Stack>

          <Grid container spacing={1.75}>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                icon={<DescriptionIcon />}
                label="Total"
                value={stats.total}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                icon={<HourglassTopIcon />}
                label="Pending"
                value={stats.pending}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                icon={<CheckCircleIcon />}
                label="Approved"
                value={stats.approved}
                color="success"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                icon={<CancelIcon />}
                label="Rejected"
                value={stats.rejected}
                color="error"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                icon={<LockOpenIcon />}
                label="Windows open"
                value={stats.open}
                color="warning"
              />
            </Grid>
          </Grid>
        </Box>

        {listLoading ? <LinearProgress /> : null}

        <Box sx={{ p: 2 }} data-tour="cv-review-grid">
          {listErr ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {listErr}
            </Alert>
          ) : null}

          <CvSubmissionsGrid
            rows={rows}
            loading={listLoading}
            onView={handleView}
            onEdit={handleEdit}
            onApprove={handleApprove}
            onReject={openReject}
          />
        </Box>
      </Paper>

      <Dialog open={rejectDialog.open} onClose={closeReject} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>
          Reject CV
          {rejectDialog.row?.name ? ` — ${rejectDialog.row.name}` : ""}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add an optional reason. The employee may see this when the CV is reopened.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={rejecting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeReject} disabled={rejecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReject}
            disabled={rejecting}
            sx={{ fontWeight: 900, borderRadius: 2 }}
          >
            {rejecting ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        message={toast.message}
      />
    </Box>
  );
}
